import { parseRateCell, splitJurisdictionLines, detectRateUnit } from '../reports/taxRate';
import { moneyNum } from './money';

// Tidy a tax bill's extracted jurisdiction lines BEFORE the Validate form is seeded from them.
//
// ⛔ BUILT FOR MIAMI-DADE (30-3129-029-0010, 2025 revised), WHICH PRINTS GROUP HEADINGS. "Miami-Dade School
// Board", "State and Other" and "Miami-Dade County" each head a block of authorities and carry no figures of
// their own. The extraction returned them as lines, plus two rows with no rate repeating real lines' amounts
// (10,427.50 and 47,725.00). Those duplicates are the harmful part: carrying a taxable value, classifyLine
// RECOVERS a rate for them (built for an LA County rate lost to a wrapped name), counts them as ad valorem,
// and Total Ad Valorem seeds at 481,445.00 instead of 423,292.50 — which in turn broke the mills/percent test.
//
// The prompt is the real fix (api/extract.js, the group-heading rule). This is the net under it:
//   • heading — a name with no printed rate and no amount. A taxable value does not save it: the AI gave the
//     Miami-Dade headings the 25,000,000 beside them, and a row with no rate and no amount contributes nothing
//     either way. A direct assessment always has an amount, so it is never one.
//   • duplicate — no printed rate, and an amount equal TO THE CENT to a line that has a printed rate. A flat
//     charge that happens to match a rated line exactly is removed too; that is this rule's known edge,
//     which is why nothing is removed silently.
//
// ⛔ A REMOVAL IS REPORTED, NEVER SILENT, and one case is called out on its own. When names fall out of step
// with their figures, the leftover names collect at the END with nothing beside them — and the heading rule
// would remove exactly those, leaving a bill that looks clean with every name still wrong. Empty rows after
// the last row carrying figures are that signature, so `shiftSuspected` says so.
//
// `removed` keeps each row whole with its original position, so the Validate step can put it back.
//
// ⛔ SUBTOTAL ROWS, EVERY STATE (Philip, 2026-10-14). San Mateo 092-020-140 / 2021 prints "General Tax Total
// 1.11030000% 245,681.58" under its four agencies — their sum, not a tax — and keeping it doubled Σ ad valorem
// (491,363.16) and the rate (2.2206). A row is a `subtotal` when EITHER its name says Total / Subtotal, OR it has a
// rate, its amount equals (±$0.02) the sum of the rated lines directly above it (since the start or the last
// subtotal) and its rate equals the sum of their rates. Cook bills are not read from these lines (utils/cookBill.js
// replaces them), and fixed charges carry no rate, so the sum rule never touches them.
export function cleanBillLines(lines) {
  const list = Array.isArray(lines) ? lines : [];
  const hasRate = (l) => parseRateCell(l?.tax_rate) != null;
  const amountOf = (l) => moneyNum(l?.amount);
  const cents = (n) => Math.round(n * 100);
  const ratedCents = new Set(list.filter(hasRate).map(amountOf).filter((a) => a != null).map(cents));
  let lastWithFigures = -1;
  list.forEach((l, i) => { if (hasRate(l) || amountOf(l) != null) lastWithFigures = i; });

  const kept = [];
  const removed = [];
  let run = [];   // the rated lines since the start or the last subtotal
  const isSubtotalName = (name) => /\b(sub)?\s*totals?\b/i.test(String(name ?? ''));
  const sumsRun = (l) => {
    if (run.length < 2) return false;
    const amt = amountOf(l), rate = parseRateCell(l.tax_rate);
    const runAmts = run.map(amountOf), runRates = run.map((x) => parseRateCell(x.tax_rate));
    if (amt == null || rate == null || runAmts.some((a) => a == null) || runRates.some((r) => r == null)) return false;
    const sumAmt = runAmts.reduce((x, y) => x + y, 0), sumRate = runRates.reduce((x, y) => x + y, 0);
    return Math.abs(amt - sumAmt) <= 0.02 && Math.abs(rate - sumRate) <= 1e-6 * Math.max(1, Math.abs(sumRate));
  };
  list.forEach((l, index) => {
    if (l && (isSubtotalName(l.name) || (hasRate(l) && sumsRun(l)))) {
      removed.push({ line: l, index, reason: 'subtotal' });
      run = [];
      return;
    }
    if (!l || hasRate(l)) { if (l) { kept.push(l); run.push(l); } return; }
    const amount = amountOf(l);
    if (amount == null && String(l.name ?? '').trim()) {
      removed.push({ line: l, index, reason: 'heading' });
    } else if (amount != null && amount !== 0 && ratedCents.has(cents(amount))) {
      removed.push({ line: l, index, reason: 'duplicate' });
    } else {
      kept.push(l);
    }
  });
  const shiftSuspected = lastWithFigures >= 0
    && removed.some((r) => r.reason === 'heading' && r.index > lastWithFigures);
  return { lines: kept, removed, shiftSuspected };
}

// Merge the narrow ad valorem table answer (/api/extract docType 'rate_table') into the full extraction's lines.
// The table arrives as two lists — the name column with headings marked, and the figure rows — because asked for
// one list the AI pairs names with figures in order and every group heading shifts the names below it (see the
// rate_table branch in api/extract.js). They are paired HERE, and only when the counts agree.
//
//   paired   — the table's rows ARE the ad valorem lines. The extraction's other lines are sorted by what the
//              table says about them:
//                • its amount is a table ad valorem row → it is that row, already present → dropped
//                • its amount is on the table's non_ad_valorem list → kept, as a FLAT charge
//                • neither, with no usable rate → kept as a flat charge (something the table did not list)
//                • neither, WITH a rate → an ad valorem row the extraction misread (2024 revised: Fire Rescue
//                  59,678.35 for 59,878.35). The table owns ad valorem, so it is set aside — in `removed`,
//                  named on the Validate step with Put back, never silently. Only when the table ANSWERED the
//                  non_ad_valorem question: without that list, L0349's "@ 0.3859" would look exactly like this,
//                  so the line is kept as a flat charge instead.
//              A non_ad_valorem charge the extraction missed altogether is added from the table.
//   unpaired — the extraction's lines stand and `paired` is false, so the Validate step says to check the names.
//              A line whose amount the table lists under non_ad_valorem is still made flat.
//
// ⛔ A NON-AD VALOREM LINE TAKES ITS NAME FROM THE TABLE'S non_ad_valorem LIST, matched by amount, in both cases. The
// first read's names shift on a Miami-Dade bill, and the shift runs on past the ad valorem table: 30-3129-029-0010 /
// 2025 revised, scanned live, named L0349's 263.01 "Fire Rescue…".
//
// ⛔ "FLAT" MEANS NO PRINTED RATE AND NO TAXABLE VALUE. Clearing the rate alone is not enough: classifyLine RECOVERS
// a rate from amount ÷ taxable when a taxable value is present, which would count it as ad valorem anyway. That is
// how L0349 on the 2024 revised Miami-Dade bill ("@ 0.3859" read as its rate) added $156.68 to ad valorem. A rate
// that never parsed (an LA County direct assessment's phone number) is left as it was, so CA bills read as before.
// ⛔ A LINE IN THE BILL'S NON-AD VALOREM SECTION NEVER COUNTS TOWARD THE RATE (Philip 2026-10-22). Broward prints
// "04 DANIA STORM WATER @ 60.0000" — a per-unit charge — and the scanner summed it into Dania Beach 504226-07-0010 /
// 2022's rate: 18.7532 + 60 = 78.7532 mills → 7.87532%. The extraction marks each line's section from the bill's own
// heading; a line marked non_ad_valorem loses its rate and taxable value here, before anything is summed. This is the
// first of two signals — mergeRateTable's non_ad_valorem list (the narrow re-ask) is the second — so a failed re-ask
// no longer lets one through. An ad valorem line keeps its rate with or without a taxable value (LA County).
export const isNonAdValorem = (l) => String(l?.section || '').trim().toLowerCase() === 'non_ad_valorem';
export function flattenNonAdValorem(lines) {
  return (Array.isArray(lines) ? lines : []).map((l) => (isNonAdValorem(l) && (l.tax_rate != null || l.taxable_value != null)
    ? { ...l, tax_rate: null, taxable_value: null } : l));
}

// The rates the scan read but that do not count — a line printed with a rate that comes out of flattenNonAdValorem /
// mergeRateTable with none, matched by amount. Named on the bill form so the TS can see what was left out.
export function ratesIgnored(before, after) {
  const cents = (v) => { const n = moneyNum(v); return n == null ? null : Math.round(n * 100); };
  const hasRate = (l) => l.tax_rate != null && String(l.tax_rate).trim() !== '';
  const list = (Array.isArray(after) ? after : []).filter(Boolean);
  const unrated = new Set(list.filter((l) => !hasRate(l)).map((l) => cents(l.amount)));
  const rated = new Set(list.filter(hasRate).map((l) => cents(l.amount)));
  return (Array.isArray(before) ? before : [])
    .filter((l) => l && hasRate(l) && cents(l.amount) != null
      && unrated.has(cents(l.amount)) && !rated.has(cents(l.amount)))
    .map((l) => ({ name: l.name ?? null, rate: String(l.tax_rate), amount: l.amount }));
}

export function mergeRateTable(lines, table) {
  const list = (Array.isArray(lines) ? lines : []).filter(Boolean);
  const cents = (v) => { const n = moneyNum(v); return n == null ? null : Math.round(n * 100); };
  const hasRate = (l) => parseRateCell(l?.tax_rate) != null;
  // ⛔ CLEAR ANY FIGURE THE SCANNER WOULD SUM, not only what parses as a rate. This tested hasRate (parseRateCell, 0–20),
  // so Broward's "04 DANIA STORM WATER @ 60.0000" — listed here as non-ad valorem — kept its 60 (over 20, so "not a
  // rate"), and the scanner's own parser (a plain decimal up to 1000) then summed it in: Dania Beach 504226-07-0010 /
  // 2022 at 7.87532%. A phone number printed in the rate column is not a plain decimal and stays as printed.
  const summable = (v) => /^\d*\.?\d+$/.test(String(v ?? '').replace(/[,\s%$]/g, ''));
  const flat = (l) => (!summable(l?.tax_rate) && moneyNum(l?.taxable_value) == null ? l : { ...l, tax_rate: null, taxable_value: null });
  const names = Array.isArray(table?.names) ? table.names : null;
  const figures = Array.isArray(table?.figures) ? table.figures : null;
  const nonAdListed = Array.isArray(table?.non_ad_valorem);
  const nonAdRows = (nonAdListed ? table.non_ad_valorem : []).filter((x) => cents(x?.amount) != null);
  const nonAd = new Set(nonAdRows.map((x) => cents(x.amount)));
  const nonAdName = new Map(nonAdRows.filter((x) => String(x.name ?? '').trim()).map((x) => [cents(x.amount), String(x.name).trim()]));
  const named = (l) => (nonAdName.has(cents(l.amount)) ? { ...l, name: nonAdName.get(cents(l.amount)) } : l);
  const flatIfNonAd = (l) => (nonAd.has(cents(l.amount)) ? named(flat(l)) : l);
  if (!names || !figures) return { lines: list.map(flatIfNonAd), paired: false, answered: false, removed: [] };

  const lineNames = names.filter((n) => n && !n.heading).map((n) => String(n.text ?? '').trim());
  if (!(figures.length > 0 && lineNames.length === figures.length)) {
    return { lines: list.map(flatIfNonAd), paired: false, answered: true, removed: [] };
  }

  // A row the table prints no taxable value for keeps the extraction's, matched by amount. An LA County bill
  // prints none per line (one net taxable, Prop 13), and the full extraction fills it in, sometimes on every
  // line; without it the per-line ✓ check has nothing to check, which would be a step back from today.
  const taxableByCents = new Map(list.filter((l) => moneyNum(l.taxable_value) != null).map((l) => [cents(l.amount), l.taxable_value]));
  const ad = figures.map((f, i) => ({ name: lineNames[i], tax_rate: f?.rate ?? null,
    taxable_value: f?.taxable_value ?? taxableByCents.get(cents(f?.amount)) ?? null, amount: f?.amount ?? null }));
  const adCents = new Set(ad.map((l) => cents(l.amount)).filter((c) => c != null));
  const rest = [];
  const removed = [];
  list.forEach((l, index) => {
    const c = cents(l.amount);
    if (c == null || adCents.has(c)) return;
    if (nonAd.has(c) || !hasRate(l) || !nonAdListed) rest.push(named(flat(l)));
    else removed.push({ line: l, index, reason: 'not in rate table' });
  });
  const restCents = new Set(rest.map((l) => cents(l.amount)));
  nonAdRows.forEach((x) => { if (!restCents.has(cents(x.amount))) rest.push({ name: x.name ?? null, tax_rate: null, taxable_value: null, amount: x.amount }); });
  return { lines: [...ad, ...rest], paired: true, answered: true, removed };
}

// A jurisdiction row with NO figures — rate, taxable value and amount all blank — is not a line, whatever its name
// says. The Validate step's ✕ removes a row outright; clearing a row's fields removes it here, at save, so an empty
// row is never stored on tax_bills.jurisdiction_rates. A printed zero ("0.00", "0.000") is a figure and stays.
const blank = (v) => v == null || String(v).trim() === '';
export const isEmptyLine = (l) => !l || (blank(l.tax_rate) && blank(l.taxable_value) && blank(l.amount));
export function dropEmptyLines(lines) {
  return Array.isArray(lines) ? lines.filter(l => !isEmptyLine(l)) : lines;
}

// Jurisdiction lines typed by hand on the parcel card (ParcelModal → Jurisdiction Breakdown → Edit lines), cleaned for
// tax_bills.jurisdiction_rates: each of the four fields trimmed (blank → null), the line's other keys kept (a scanned line
// being corrected keeps whatever else the scan stored), and a line with no figures dropped (isEmptyLine). No lines → null,
// the value a bill with no breakdown already carries (submitTaxBill), never an empty array.
export function cleanEnteredLines(lines) {
  const t = (v) => (v == null || String(v).trim() === '' ? null : String(v).trim());
  const out = dropEmptyLines((Array.isArray(lines) ? lines : []).map((l) => ({
    ...l, name: t(l?.name), tax_rate: t(l?.tax_rate), taxable_value: t(l?.taxable_value), amount: t(l?.amount),
  })));
  return out.length ? out : null;
}

// The year's rate and taxable value read off its jurisdiction lines, for the parcel card's installment rows when nothing
// else supplies them (ParcelModal collectorTaxModel: a typed rate, then the bill's own total_tax_rate, then THIS; the
// appeal level's taxable value, then THIS). A bill processed by hand carries no rate, and a back-filled year often has
// no appeal level, so without this the rows read "—" beside lines that hold both figures (162-22-402-002 / 2021).
// ⛔ ONE BASE OR NOTHING. The rated lines must share a single taxable value: that is what lets one rate × one base be
// the tax, and what lets detectRateUnit tell mills from percent (Σ rated amount ÷ base). Lines on different bases
// (Florida school vs non-school) or with no taxable value give { null, null } — never a rate against a guessed base.
// Flat charges (no rate) are not part of either figure. → { ratePercent, taxable } (numbers or null)
export function lineRateAndBase(lines) {
  const { rated, sumRate, sumRatedAmount } = splitJurisdictionLines(lines);
  const none = { ratePercent: null, taxable: null };
  if (!rated.length || sumRate == null || !sumRate) return none;
  const bases = [...new Set(rated.map((l) => moneyNum(l.taxable_value)).filter((v) => v != null && v !== 0))];
  if (bases.length !== 1) return none;
  const unit = detectRateUnit(rated, sumRatedAmount, bases[0]);
  return { ratePercent: unit === 'mills' ? sumRate / 10 : sumRate, taxable: bases[0] };
}
